import Logging from '../Logging/Logging';
import { localToUtc, utcToLocal } from '../Parsing/DateTime';
import { formatCurrency } from './Currency';
import { formatDecimal } from './Number';
import { formatPercentage } from './Percentage';

export enum DateFormatEnum {
  YMD,
  MDY,
  WMDY,
  DMY,
  WDMY,
}

export enum TimeFormatEnum {
  HMS,
  HM,
}

export interface DateOptions {
  dateFormat?: DateFormatEnum;
  dateSeperator?: '/' | '.' | '-' | ' ';
  month?: 'numeric' | '2-digit' | 'long' | 'short';
  day?: 'numeric' | '2-digit';
  weekday?: 'long' | 'short';
  weekDaySeperator?: ', ' | ' ';
  utcDate?: boolean;
}

export interface TimeOptions {
  timeFormat?: TimeFormatEnum;
  timeSeperator?: ':' | '.' | '-' | ' ';
  hour?: 'numeric' | '2-digit';
  minute?: 'numeric' | '2-digit';
  second?: 'numeric' | '2-digit';
  hour12?: boolean;
  utcTime?: boolean;
}

/**
 * Gets a formatted Date.  Can be customized to any format. Defaults to 'MM/DD/YYYY'.
 * Can be formatted in Local or UTC time.
 */
export function formatDate(
  date: Date,
  {
    dateFormat = DateFormatEnum.MDY,
    dateSeperator = '/',
    month = '2-digit',
    day = '2-digit',
    weekday = 'long',
    weekDaySeperator = ', ',
    utcDate = true,
  }: DateOptions = {},
): string {
  if (date) {
    // The returned date string must be in UTC Time
    const dateValue: Date = utcDate ? localToUtc(date) : date;

    Logging.Debug('Format Date:', DateFormatEnum[dateFormat], dateSeperator, month, day, weekday, utcDate, date, dateValue);

    const monthNames: string[] = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'];

    const weekdayNames: string[] = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'];

    const year = dateValue.getFullYear();

    let monthString: string = null;
    switch (month) {
      case 'numeric':
        monthString = (1 + dateValue.getMonth()).toString();
        break;
      case '2-digit':
        monthString = (1 + dateValue.getMonth()).toString().padStart(2, '0');
        break;
      case 'long':
        monthString = monthNames[dateValue.getMonth()];
        break;
      case 'short':
        monthString = monthNames[dateValue.getMonth()].substring(0, 3);
        break;
      default:
        monthString = (1 + dateValue.getMonth()).toString().padStart(2, '0');
        break;
    }

    const dayString: string = day === '2-digit' ? dateValue.getDate().toString().padStart(2, '0') : dateValue.getDate().toString();
    const weekdayString: string = weekday === 'long' ? weekdayNames[dateValue.getDay()] : weekdayNames[dateValue.getDay()].substring(0, 3);

    switch (dateFormat) {
      case DateFormatEnum.YMD:
        return `${year}${dateSeperator}${monthString}${dateSeperator}${dayString}`;

      case DateFormatEnum.DMY:
        return `${dayString}${dateSeperator}${monthString}${dateSeperator}${year}`;

      case DateFormatEnum.WDMY:
        return `${weekdayString}${weekDaySeperator}${dayString}${dateSeperator}${monthString}${dateSeperator}${year}`;

      case DateFormatEnum.MDY:
        return `${monthString}${dateSeperator}${dayString}${dateSeperator}${year}`;

      case DateFormatEnum.WMDY:
        return `${weekdayString}${weekDaySeperator}${monthString}${dateSeperator}${dayString}${dateSeperator}${year}`;

      default:
        return '';
    }
  }

  return '';
}

/**
 * Gets a formatted Date.  Will always be in UTC time and in format YYYY-MM-DD
 */
export function formatDateISO(value: Date): string {
  if (!value) return null;

  // toISOString returns YYYY-MM-DDTHH:mm:ss.sssZ
  const stringDate: string = value.toISOString().slice(0, 10);

  return stringDate;
}

/**
 * Gets a formatted Date.  Will always be in Local time and in format YYYY-MM-DD
 */
export function formatLocalDateISO(value: Date): string {
  if (!value) return null;

  // toISOString returns YYYY-MM-DDTHH:mm:ss.sssZ
  const stringDate: string = utcToLocal(value).toISOString().slice(0, 10);

  return stringDate;
}

/**
 * Get a formatted Time. Can be customized to any format. Defaults to 'HH:mm:ss'.
 * Can be formatted in Local or UTC time.
 */
export function formatTime(
  date: Date,
  {
    timeFormat = TimeFormatEnum.HMS,
    timeSeperator = ':',
    hour = '2-digit',
    minute = '2-digit',
    second = '2-digit',
    hour12 = false,
    utcTime = true,
  }: TimeOptions = {},
): string {
  if (date) {
    // The returned date string must be in UTC Time
    const dateValue: Date = utcTime ? localToUtc(date) : date;

    Logging.Debug('Format Time:', TimeFormatEnum[timeFormat], timeSeperator, hour, minute, second, hour12, utcTime, date, dateValue);

    let amPm = '';
    let hoursValue: number = dateValue.getHours();

    if (hour12) {
      if (hoursValue >= 12) {
        amPm = ' PM';
      } else {
        amPm = ' AM';
      }

      if (hoursValue > 12) {
        hoursValue -= 12;
      }
    }

    const hourString: string = hour === '2-digit' ? hoursValue.toString().padStart(2, '0') : hoursValue.toString();
    const minuteString: string = minute === '2-digit'
      ? dateValue.getMinutes().toString().padStart(2, '0') : dateValue.getMinutes().toString();
    const secondString: string = second === '2-digit'
      ? dateValue.getSeconds().toString().padStart(2, '0') : dateValue.getSeconds().toString();

    switch (timeFormat) {
      case TimeFormatEnum.HMS:
        return `${hourString}${timeSeperator}${minuteString}${timeSeperator}${secondString}${amPm}`;

      case TimeFormatEnum.HM:
        return `${hourString}${timeSeperator}${minuteString}${amPm}`;

      default:
        return '';
    }
  }

  return '';
}

/**
 * Gets a formatted Time.  Will always be in UTC time and in format HH:mm:ss
 */
export function formatTimeISO(value: Date): string {
  if (!value) return null;

  // toISOString returns YYYY-MM-DDTHH:mm:ss.sssZ
  const stringTime: string = value.toISOString().slice(11, 8);

  return stringTime;
}

/**
 * Get a formatted Date / Time. Can be customized to any format. Defaults to 'MM/DD/YYYY HH:MM:SS'
 */
export function formatDateTime(
  date: Date,
  seperator = ' ',
  {
    dateFormat = DateFormatEnum.MDY,
    dateSeperator = '/',
    month = '2-digit',
    day = '2-digit',
    weekday = 'long',
    weekDaySeperator = ', ',
    utcDate = true,
  }: DateOptions = {},
  {
    timeFormat = TimeFormatEnum.HMS,
    timeSeperator = ':',
    hour = '2-digit',
    minute = '2-digit',
    second = '2-digit',
    hour12 = false,
    utcTime = true,
  }: TimeOptions = {},
): string {
  const dateString = formatDate(date, {
    dateFormat,
    dateSeperator,
    month,
    day,
    weekday,
    weekDaySeperator,
    utcDate,
  });
  const timeString = formatTime(date, {
    timeFormat,
    timeSeperator,
    hour,
    minute,
    second,
    hour12,
    utcTime,
  });

  return `${dateString}${seperator}${timeString}`;
}

/**
 * Gets a formatted Date / Time.  Will always be in UTC time and in format YYYY-MM-DD HH:mm:ss
 */
export function formatDateTimeISO(value: Date): string {
  if (!value) return null;

  // toISOString returns YYYY-MM-DDTHH:mm:ss.sssZ
  return value.toISOString().slice(0, 19).replace('T', ' ');
}

/**
 * Gets a formatted Date / Time.  Will always be in UTC time and in format YYYY-MM-DDTHH:mm:ss.sss-04:00
 */
export const formatDateTimeISOTimeZone = (value: Date): string => {
  if (value) {
    const off = value.getTimezoneOffset();
    const absoff = Math.abs(off);
    // eslint-disable-next-line max-len
    return (`${new Date(value.getTime() - off * 60 * 1000).toISOString().substr(0, 23)}${(off > 0 ? '-' : '+')}${Math.floor((absoff / 60)).toString().padStart(2, '0')}:${(absoff % 60).toString().padStart(2, '0')}`);
  }

  return '';
};

/**
 * Format the time elapsed as a Human Friendly Elapsed time, e.g a minute ago, 2 hours ago, a month ago, 3 years ago
 */
export const formatHumanFriendlyDate = (date: Date): string => {
  // https://stackoverflow.com/a/7641812/3229742
  if (!date) {
    return '';
  }

  const now = new Date();
  const deltaSeconds = Math.round((now.valueOf() - date.valueOf()) / 1000);

  const minute = 60;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;
  const month = day * 30;
  const year = day * 365;
  let humanFriendly;

  if (deltaSeconds < minute) {
    humanFriendly = deltaSeconds < 30 ? 'just now' : `${deltaSeconds} seconds ago`;
  } else if (deltaSeconds < hour) {
    const minutes = Math.round(deltaSeconds / minute);
    humanFriendly = minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
  } else if (deltaSeconds < day) {
    const hours = Math.round(deltaSeconds / hour);
    humanFriendly = hours === 1 ? 'an hour ago' : `${hours} hours ago`;
  } else if (deltaSeconds < (week * 2)) { // Anything less than 2 weeks in days
    const days = Math.round(deltaSeconds / day);
    humanFriendly = days === 1 ? 'yesterday' : `${days} days ago`;
  } else if (deltaSeconds < (week * 6)) { // Anything less than 6 weeks in weeks
    const weeks = Math.round(deltaSeconds / week);
    humanFriendly = weeks === 1 ? 'a week ago' : `${weeks} weeks ago`;
  } else if (deltaSeconds < year) {
    const months = Math.round(deltaSeconds / month);
    humanFriendly = months === 1 ? 'a month ago' : `${months} months ago`;
  } else {
    const years = Math.round(deltaSeconds / year);
    humanFriendly = years === 1 ? 'a year ago' : `${years} years ago`;
  }

  Logging.Debug('Friendly Date', deltaSeconds, humanFriendly, date);

  return humanFriendly;
};

/**
 * Format the time elapsed as a Human Friendly Elapsed time, e.g 'For the last' 5 days, 24 minutes, 3 weeks, 2 months, 2 years
 */
export const formatHumanFriendlyElapsedTime = (date: Date): string => {
  if (!date) {
    return '';
  }

  const now = new Date();
  const deltaSeconds = Math.round((now.valueOf() - date.valueOf()) / 1000);

  const minute = 60;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;
  const month = day * 30;
  const year = day * 365;
  let humanFriendly;

  if (deltaSeconds < minute) {
    humanFriendly = deltaSeconds < 30 ? 'couple of seconds' : `${deltaSeconds} seconds`;
  } else if (deltaSeconds < hour) {
    const minutes = Math.round(deltaSeconds / minute);
    humanFriendly = minutes === 1 ? 'minute' : `${minutes} minutes`;
  } else if (deltaSeconds < day) {
    const hours = Math.round(deltaSeconds / hour);
    humanFriendly = hours === 1 ? 'hour' : `${hours} hours`;
  } else if (deltaSeconds < (week * 2)) { // Anything less than 2 weeks in days
    const days = Math.round(deltaSeconds / day);
    humanFriendly = days === 1 ? 'day' : `${days} days`;
  } else if (deltaSeconds < (week * 6)) { // Anything less than 6 weeks in weeks
    const weeks = Math.round(deltaSeconds / week);
    humanFriendly = weeks === 1 ? 'week' : `${weeks} weeks`;
  } else if (deltaSeconds < year) {
    const months = Math.round(deltaSeconds / month);
    humanFriendly = months === 1 ? 'month' : `${months} months`;
  } else {
    const years = Math.round(deltaSeconds / year);
    humanFriendly = years === 1 ? 'year' : `${years} years`;
  }

  Logging.Debug('Friendly Date', deltaSeconds, humanFriendly, date);

  return humanFriendly;
};
